import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { SpotlightAgentBaseService } from 'app/services/spotlight-agent-base.service';

export interface SpotlightDashboardIdea {
  title: string;
  description: string;
  prompt: string;
}

export interface GenerateSpotlightDashboardParams {
  projectId: string;
  collectionId: string;
  prompt: string;
  contentChannel?: string;
  hasSections?: boolean;
}

export interface GenerateSpotlightDashboardResult {
  dashboardUrl: string | null;
  dashboardName?: string;
}

@Injectable({ providedIn: 'root' })
export class SpotlightAgentDashboardIdeasService extends SpotlightAgentBaseService {
  private readonly http: HttpClient = inject(HttpClient);

  public async fetchIdeas(
    collectionId: string,
    projectId: string,
    contentChannel?: string,
    hasSections?: boolean
  ): Promise<SpotlightDashboardIdea[]> {
    const { session, baseUrl, env } = await this.getAgentRequestContext();
    const url: string = `${baseUrl}/api/dashboard/ideas`;
    const response: SpotlightDashboardIdea[] = await firstValueFrom(
      this.http.post<SpotlightDashboardIdea[]>(
        url,
        {
          collectionId,
          projectId,
          token: session.id,
          env,
          contentChannel,
          hasSections,
        },
        { headers: this.getAuthorizationHeaders() }
      )
    );
    return response ?? [];
  }

  public async generateSpotlightDashboard(
    params: GenerateSpotlightDashboardParams
  ): Promise<GenerateSpotlightDashboardResult> {
    const { session, baseUrl, env } = await this.getAgentRequestContext();
    const url: string = `${baseUrl}/api/dashboard/spotlight`;
    const groupId: string | null = this.getAgentGroupId();
    const body = {
      projectId: params.projectId,
      collectionId: params.collectionId,
      accountId: session.info2.account_id,
      token: session.id,
      prompt: params.prompt,
      groupId,
      env,
      contentChannel: params.contentChannel,
      hasSections: params.hasSections,
    };
    const response: GenerateSpotlightDashboardResult = await firstValueFrom(
      this.http.post<GenerateSpotlightDashboardResult>(url, body, {
        headers: this.getAuthorizationHeaders(),
      })
    );
    return response ?? { dashboardUrl: null };
  }

  private getAuthorizationHeaders(): Record<string, string> {
    const token: string | null = this.authService.getToken();
    if (!token) {
      return {};
    }
    return { Authorization: token };
  }
}
